<template>
    <AppModal v-model="modelValue" :close-button="false">
        <template #header>
            <div class="d-flex justify-content-between align-items-center w-100">
                <h3 class="m-0">
                    {{ $t('iCode Reports - :serial', { serial: device.serial }) }}
                </h3>

                <AppButton v-if="user?.can['upload.device.icode']" @click="showAddICodeModal = true">
                    {{ $t('Add iCode Report') }}
                </AppButton>
            </div>
        </template>

        <template #default>
            <AppLoading v-if="isFetching" />
            <ICodeReportsList
                v-else
                v-model="activeReportsIds"
                v-model:activeDate="activeDate"
                :reports="reports" />

            <AddICodeModal
                v-model="showAddICodeModal"
                :serial="device.serial"
                :patient="patient"
                @icode:created="iCodeCreated" />
        </template>

        <template #footer>
            <AppButton btn-class="btn-outline-primary" @click="modelValue = false">
                {{ $t('Close') }}
            </AppButton>
            <AppButton :is-disabled="activeDateActiveReportsIds?.length === 0" @click="generatePdfReport">
                {{ $t('Generate Report') }}
            </AppButton>
        </template>
    </AppModal>
</template>

<script setup lang="ts">
import { computed, type PropType, ref, watch } from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import axios from "axios";
import AppModal from "@/vue/shared/AppModal.vue";
import AppLoading from "@/vue/shared/AppLoading.vue";
import AppButton from "@/vue/shared/AppButton.vue";
import ICodeReportsList from "@/vue/connect/icode/ICodeReportsList.vue";
import { type Patient } from "@/vue/types/patient";
import { type MachineType } from "@/vue/types/machine";
import { type ReportType } from "@/vue/types/report";
import AddICodeModal from "@/vue/connect/icode/AddICodeModal.vue";
import useDates from "@/vue/composables/useDates";
import { useUserStore } from "@/vue/store/userStore";
import { storeToRefs } from "pinia";
import { isOrgEquipmentProvider as isEP } from "@/vue/helpers";

const modelValue = defineModel<boolean>({ required: true });
const props = defineProps({
    device: {
        type: Object as PropType<MachineType>,
        required: true,
    },
    patient: {
        type: Object as PropType<Patient>,
        required: true,
    },
});

const userStore = useUserStore();
const isEquipmentProvider = computed(() => isEP(userStore.org?.org_type?.type));

const { user } = storeToRefs(userStore);

const { getFormattedDate } = useDates('MM-DD-YYYY HH:mm');

const isFetching = ref(false);
const reports = ref<ReportType[]>();
const FetchReports = async () => {
    isFetching.value = true;

    try {
        const { data } = await axios.get<ReportType[]>(`/api/icode-reports?serial=${props.device.serial}&patient_id=${props.patient.id}`);
        reports.value = data;
        activeReportsIds.value = data.map(report => report.id);
        activeDate.value = getFormattedDate(data[0]?.created || '');
    } catch (e) {
        console.error(e);
    } finally {
        isFetching.value = false;
    }
}

watch(
    () => props.device,
    FetchReports,
    {
        immediate: true,
    }
);

const showAddICodeModal = ref(false);
const activeReportsIds = ref<number[]>([]);
const activeDate = ref<string>('');
const activeDateActiveReportsIds = computed(() => {
    if (!reports.value) return;

    return reports.value
        .filter((report) => getFormattedDate(report.created) === activeDate.value && activeReportsIds.value.includes(report.id))
        .map((report) => report.id);
});

const iCodeCreated = async () => {
    showAddICodeModal.value = false;
    await FetchReports();
}

const generatePdfReport = () => {
    if (!activeDateActiveReportsIds.value) return;

    const url = new URL('/reports/icode', window.location.toString());

    url.searchParams.set('patient_id', `${props.patient.id}`);
    url.searchParams.set('serial', props.device.serial);

    if (isEquipmentProvider.value) {
        url.searchParams.set('show_ep', '1');
    }

    activeDateActiveReportsIds.value.forEach(id => {
        url.searchParams.append('reports_ids[]', `${id}`);
    });

    if (window.vueApp?.debug) {
        url.searchParams.set('view', '1');
    }

    // Open the URL in a new tab or window
    window.open( url.href, '_blank');
};
</script>
